import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtNumber } from "@/lib/format";
import { Loader2 } from "lucide-react";

const fmtD = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10).split("-").reverse().join("/") : s || "-");
const FIELDS = [["diesel", "Diesel"], ["bank_transfer", "Bank Transfer"]];

const Cell = ({ v, testid }) => (
  <td className="py-2 px-3 text-right font-mono whitespace-nowrap" data-testid={testid}>
    {v ? <><span className="text-muted-foreground">{fmtNumber(v.old)}</span> → <b>{fmtNumber(v.new)}</b></> : <span className="text-muted-foreground">same</span>}
  </td>
);

export function DieselDiffDialog({ hubId, diffs, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const present = FIELDS.filter(([k]) => diffs.some((d) => d[k]));
  const [on, setOn] = useState(Object.fromEntries(present.map(([k]) => [k, true])));
  const items = diffs.map((d) => ({ challan_id: d.challan_id, ...Object.fromEntries(present.filter(([k]) => on[k] && d[k]).map(([k]) => [k, d[k].new])) })).filter((it) => Object.keys(it).length > 1);
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/challans/update-diesel", { hub: hubId, items });
      toast.success(`Corrected ${present.filter(([k]) => on[k]).map(([, l]) => l).join(" & ")} on ${data.updated} trip(s)`);
      onDone();
    } catch (e) { const det = e?.response?.data?.detail; toast.error(typeof det === "string" ? det : "Could not update trips"); }
    setBusy(false);
  };
  const tot = (k, w) => diffs.reduce((s, d) => s + (d[k] ? d[k][w] : 0), 0);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid="diesel-diff-dialog">
        <DialogHeader><DialogTitle>{diffs.length} existing trip(s) differ from this file</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">These delivery numbers were already imported, so they were skipped. Tick what to correct to the file's value — nothing else changes.</p>
        <div className="flex gap-5 text-sm">
          {present.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer" data-testid={`diff-toggle-${k}`}>
              <Checkbox checked={!!on[k]} onCheckedChange={(v) => setOn((p) => ({ ...p, [k]: !!v }))} />
              {l} <span className="text-muted-foreground">({diffs.filter((d) => d[k]).length} trips · {fmtNumber(tot(k, "old"))} → {fmtNumber(tot(k, "new"))})</span>
            </label>
          ))}
        </div>
        <div className="overflow-auto max-h-[50vh] rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase text-muted-foreground sticky top-0"><tr>{["GR Date", "Truck", "Delivery No", ...present.map(([, l]) => l)].map((h) => <th key={h} className="py-2 px-3 text-left font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {diffs.map((d, i) => (
                <tr key={d.challan_id} className="border-t border-border" data-testid={`diesel-diff-row-${i}`}>
                  <td className="py-2 px-3 font-mono">{fmtD(d.gr_date)}</td>
                  <td className="py-2 px-3 font-mono">{d.vehicle_number}</td>
                  <td className="py-2 px-3 font-mono text-xs">{d.delivery_number}</td>
                  {present.map(([k]) => <Cell key={k} v={d[k]} testid={`diff-${k}-${i}`} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="diesel-diff-cancel">Keep as is</Button>
          <Button onClick={apply} disabled={busy || !items.length} data-testid="diesel-diff-apply">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Update {items.length} trip(s)</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
